import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { ReportView } from "@/components/report/report-view";
import { getReportGate, unlockReport } from "@/lib/reports.functions";
import { reportStrings } from "@/lib/reports-i18n";
import type { PublicReport, ReportLocale } from "@/lib/reports-types";

export const Route = createFileRoute("/r/$token")({
  ssr: false,
  component: ReportPage,
  head: () => ({
    meta: [
      { title: "Private report · the Vibing Company" },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet" },
      { name: "googlebot", content: "noindex, nofollow" },
      {
        name: "description",
        content: "A confidential client report. Access requires a password.",
      },
    ],
  }),
});

function ReportPage() {
  const { token } = Route.useParams();
  const gateFn = useServerFn(getReportGate);
  const unlockFn = useServerFn(unlockReport);
  const [unlocked, setUnlocked] = useState<PublicReport | null>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);

  const gate = useQuery({
    queryKey: ["report-gate", token],
    queryFn: () => gateFn({ data: { token } }),
    staleTime: 60_000,
  });

  const unlock = useMutation({
    mutationFn: () => unlockFn({ data: { token, password } }),
    onSuccess: (res) => {
      if (res.ok && res.report) setUnlocked(res.report);
      else setError(true);
    },
    onError: () => setError(true),
  });

  const report = unlocked ?? (gate.data?.state === "open" ? gate.data.report : null);
  if (report) return <ReportView report={report} />;

  const locale: ReportLocale = gate.data?.state === "locked" ? gate.data.language : "de";
  const t = reportStrings(locale);

  if (gate.isLoading) {
    return <div className="min-h-svh bg-background" aria-busy="true" />;
  }

  const locked = gate.data?.state === "locked" ? gate.data : null;

  if (!locked) {
    return (
      <main className="flex min-h-svh items-center justify-center bg-background px-6">
        <p className="text-sm text-foreground/60">{t.notFound}</p>
      </main>
    );
  }

  return (
    <main className="flex min-h-svh items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm">
        <p className="text-[0.68rem] font-medium uppercase tracking-[0.22em] text-foreground/45">
          {t.locked}
        </p>
        <h1 className="mt-4 font-display text-[clamp(1.8rem,4vw,2.4rem)] leading-[1.08] tracking-[-0.03em] text-foreground text-balance">
          {locked.title}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground/60">{t.lockedLead}</p>
        <form
          className="mt-8 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(false);
            unlock.mutate();
          }}
        >
          <label htmlFor="report-password" className="sr-only">
            {t.password}
          </label>
          <input
            id="report-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t.password}
            className="w-full rounded-full border border-border bg-card px-5 py-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
          />
          <button
            type="submit"
            disabled={unlock.isPending || password.length === 0}
            className="w-full rounded-full bg-foreground px-5 py-3 text-sm font-medium text-background transition-transform duration-200 active:scale-[0.98] disabled:opacity-50"
          >
            {unlock.isPending ? t.unlocking : t.unlock}
          </button>
          {error ? (
            <p role="alert" className="text-xs text-destructive">
              {t.wrong}
            </p>
          ) : null}
          {locked.hint ? (
            <p className="text-xs text-foreground/45">{locked.hint}</p>
          ) : null}
        </form>
      </div>
    </main>
  );
}
